import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Link as MUILink,
  Collapse,
  CircularProgress,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { DateTime } from "luxon";
import React, { useEffect, useMemo, useState } from "react";
import Boundary from "models/geospatial/boundary";
import Point from "models/geospatial/point";
import MapLeaflet from "components/sitelet/input-page/metadata-card/metadata/map-leaflet/MapLeaflet";
import "./html-render.css";
import TableField from "./table-field/TableField";
import RelatedField from "./related-field/RelatedField";
import RichTextField from "./rich-text-field";
import InputField from "models/input/input-field";
import FieldLabel from "./field-label";
import TagsField from "./tags-field/TagsField";

const largeFields = [
  "form-boundary",
  "form-bounding-box",
  "form-map",
  "form-point",
  "form-textarea",
  "form-textarea-rich",
  "form-dynamic-field",
  "form-field-table",
  "form-field-group",
  "form-lineage",
];

const RenderField = ({
  field,
  label,
  boundaries,
  points,
  isSearchCard,
}: {
  field: InputField;
  label?: string;
  boundaries?: Boundary[];
  points?: Point[];
  isSearchCard?: boolean;
}) => {
  const [collapse, setCollapse] = useState(false);
  const boundary = useMemo(() => {
    if (
      ["form-boundary", "form-bounding-box"].includes(field.field_type) &&
      boundaries
    )
      return boundaries.find((boundary) => boundary.guid == field.data);
  }, [field.field_type, boundaries]);
  const point = useMemo(() => {
    if ("form-point" === field.field_type && points)
      return points.find((point) => point.guid == field.data);
  }, [field.field_type, points]);
  const largeField = useMemo(
    () =>
      largeFields.includes(field.field_type) ||
      field.table ||
      (["FAF-nt", "nrdata-orig"].includes(
        process.env.REACT_APP_STYLING_THEME ?? "",
      ) &&
        !isSearchCard),
    [field.field_type, field.table],
  );

  function truncateString(string: string) {
    if (string.length > 300) {
      return string.substring(0, 300 - 3) + "...";
    }
    return string;
  }

  function dateTimeConversion(field: InputField) {
    let dt;
    try {
      if (/^\d+$/.test(field.data)) {
        dt = DateTime.fromMillis(Number(field.data));
      } else {
        dt = DateTime.fromISO(field.data, {
          zone: "utc",
        });
        if (!dt.isValid)
          dt = DateTime.fromFormat(field.data, "yyyy/LL/dd HH:mm:ss", {
            zone: "utc",
          });
      }

      if (dt.isValid) {
        return dt
          .toLocal()
          .toFormat(
            (process.env.REACT_APP_DATE_FORMAT ?? "dd/MM/yyyy") +
              " " +
              (process.env.REACT_APP_TIME_FORMAT ?? "HH:mm:ss"),
          );
      }
      console.warn("Invalid datetime:", field.data);
    } catch (e) {
      console.warn("Invalid datetime:", field.data, e);
    }
    return field.data;
  }

  useEffect(() => {
    if (field.collapsible === "collapsed") setCollapse(false);
    else setCollapse(true);
  }, [field]);

  return (
    <Box
      sx={{ p: 1, width: largeField ? "100%" : "50%" }}
      border={
        field.field_type === "form-field-group" ? "1px black solid" : undefined
      }
    >
      <Box
        width={
          [
            "form-boundary",
            "form-bounding-box",
            "form-map",
            "form-point",
          ].includes(field.field_type) &&
          !["FAF-nt", "nrdata-orig"].includes(
            process.env.REACT_APP_STYLING_THEME ?? "",
          )
            ? "50%"
            : "100%"
        }
      >
        <FieldLabel
          collapsible={field.collapsible}
          collapse={collapse}
          HandleCollapse={(collapse) => setCollapse(collapse)}
          label={label}
        />
        <Collapse in={collapse} sx={{ width: "100%" }}>
          {field.field_type === "form-field-table" ? (
            field.data?.data &&
            field.data.data.length > 0 &&
            field.data?.headers &&
            field.data.headers.length > 0 && <TableField field={field} />
          ) : field.field_type === "form-field-group" ? (
            field.data &&
            typeof field.data === "object" &&
            Object.keys(field.data).map((section_field) => {
              return (
                <RenderField
                  key={`section-${section_field}`}
                  field={field.data[section_field]}
                  boundaries={boundaries}
                  points={points}
                  label={section_field}
                  isSearchCard={isSearchCard}
                />
              );
            })
          ) : field.field_type === "form-date" ? (
            <Typography>
              {DateTime.fromFormat(String(field.data), "yyyy/MM/dd").toFormat(
                process.env.REACT_APP_DATE_FORMAT ?? "dd/MM/yyyy",
              )}
            </Typography>
          ) : field.field_type === "form-datetime" ? (
            <Typography>{dateTimeConversion(field)}</Typography>
          ) : ["form-boundary", "form-bounding-box"].includes(
              field.field_type,
            ) ? (
            boundary ? (
              <Box display="inline-flex" width="100%">
                <MUILink
                  to={`/search?boundaries=${boundary.name}`}
                  component={RouterLink}
                  underline="hover"
                  sx={{
                    wordWrap: "break-word",
                    color: ["nrdata-orig", "FAF-nt"].includes(
                      process.env.REACT_APP_STYLING_THEME ?? "",
                    )
                      ? "#06c"
                      : undefined,
                  }}
                >
                  {boundary.name}
                </MUILink>
              </Box>
            ) : (
              <Box display="inline-flex" width="100%">
                <CircularProgress />
              </Box>
            )
          ) : "form-point" === field.field_type ? (
            point ? (
              <Box display="inline-flex" width="100%">
                <MUILink
                  color="inherit"
                  to={`/search?points=${point.name}`}
                  component={RouterLink}
                  underline="hover"
                  sx={{
                    wordWrap: "break-word",
                    color: ["nrdata-orig", "FAF-nt"].includes(
                      process.env.REACT_APP_STYLING_THEME ?? "",
                    )
                      ? "#06c"
                      : undefined,
                  }}
                >
                  {point.name}
                </MUILink>
              </Box>
            ) : (
              <Box display="inline-flex" width="100%">
                <CircularProgress />
              </Box>
            )
          ) : ["form-textarea-rich"].includes(field.field_type) ||
            (field.field_type === "form-dynamic-field" &&
              field.display_type === "html") ? (
            <RichTextField
              searchCard={isSearchCard}
              field={field}
              label={label}
            />
          ) : ["form-textarea"].includes(field.field_type) ? (
            <div style={{ whiteSpace: "pre-line" }}>
              {String(isSearchCard ? truncateString(field.data) : field.data)}
            </div>
          ) : field.field_type === "form-url" ? (
            <MUILink
              href={field.data}
              underline="hover"
              component="a"
              sx={{
                wordWrap: "break-word",
                color: ["nrdata-orig", "FAF-nt"].includes(
                  process.env.REACT_APP_STYLING_THEME ?? "",
                )
                  ? "#06c"
                  : undefined,
                fontSize: "1rem",
              }}
            >
              {field.data}
            </MUILink>
          ) : "form-tagging" === field.field_type ? (
            <TagsField tags={field.data} isSearchCard={!!isSearchCard} />
          ) : "form-lineage" === field.field_type ? (
            field.data &&
            field.data.length > 0 && <RelatedField field={field} />
          ) : field.field_type === "form-checkbox" ? (
            <List dense sx={{ p: 0 }}>
              {field.data &&
                field.data.length > 0 &&
                field.data.map((checkItem: string) => (
                  <ListItem key={checkItem}>
                    <ListItemText
                      primary={checkItem}
                      slotProps={{
                        primary: { fontSize: "1rem", lineHeight: 1.5 },
                      }}
                    />
                  </ListItem>
                ))}
            </List>
          ) : field.field_type === "form-dynamic-field" &&
            field.display_type === "datetime" ? (
            <Typography>{dateTimeConversion(field)}</Typography>
          ) : (
            <Typography>{String(field.data)}</Typography>
          )}
        </Collapse>
      </Box>
      {((boundary?.tags && boundary.tags.length > 0) ||
        (point?.tags && point.tags.length > 0)) && (
        <Box display="flex" flexDirection="column" width="100%">
          <Typography variant="subtitle2">
            <b>{label} tags</b>:
          </Typography>
          <TagsField
            geospatial={true}
            tags={boundary ? boundary.tags : point ? point.tags : []}
            isSearchCard={!!isSearchCard}
          />
        </Box>
      )}
      {(boundary?.description || point?.description) && (
        <Box flexDirection={"row"} width="100%">
          <Box display="flex" flexDirection="column" width="100%">
            <Typography variant="subtitle2">
              <b>{label} description</b>:
            </Typography>
            <Typography>
              {point
                ? String(
                    isSearchCard
                      ? truncateString(point.description)
                      : point.description,
                  )
                : boundary
                  ? String(
                      isSearchCard
                        ? truncateString(boundary.description)
                        : boundary.description,
                    )
                  : ""}
            </Typography>
          </Box>
        </Box>
      )}
      {boundary && !isSearchCard && (
        <Box width="100%">
          <MapLeaflet key={`leaflet-${boundary.guid}`} data={boundary} />
        </Box>
      )}
      {point && !isSearchCard && (
        <Box width="100%">
          <MapLeaflet key={`leaflet-${point.guid}`} data={point} />
        </Box>
      )}
    </Box>
  );
};
export default RenderField;
